import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import { CheckCheck, SquarePen } from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { useDmConversations } from "../hooks/useDmConversations";
import {
  DM_EMPTY_IDLE_MS,
  deleteDmConversation,
  ensureChannelDefaults,
  ensureDmNotes,
  getChannel,
  markAllDmRead,
  setDmMuted,
} from "@/lib/dmApi";
import { listPartnerChannels } from "@/lib/partnerApi";
import { setConversationMutedLocal } from "@/lib/dmMuteCache";
import { usePartnerSocket } from "../scripts/PartnerSocketContext";
import DmConversationSections from "./dm/DmConversationSections";
import DmThread from "./dm/DmThread";
import PartnerThread from "./dm/PartnerThread";
import { AddToHomeScreenBanner } from "./AddToHomeScreenOffer";
import NewDmChatDialog from "./dm/NewDmChatDialog";
import MessageSearchBar from "./dm/MessageSearchBar";
import { Button } from "@/components/ui/button";
import { API_BASE_URL } from "@/lib/apiBase";
import { cn } from "@/lib/utils";
import { useDmAlerts } from "../scripts/DmAlertContext";

export function MessagesEmptyPage() {
  return (
    <div className="flex h-full min-h-0 flex-1 items-center justify-center p-8">
      <div className="text-center max-w-sm space-y-2">
        <h1 className="text-lg font-semibold">Messages</h1>
        <p className="text-sm text-muted-foreground">
          Select a workspace with the Workspace switcher at the top of the
          sidebar to view and start messages.
        </p>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const {
    orgId,
    conversationId: conversationIdParam,
    partnerChannelId: partnerChannelIdParam,
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useAuth();
  const { setSelectedOrgId } = useOrgFilter();
  const { suggestDesktopNotifications } = useDmAlerts();

  useEffect(() => {
    suggestDesktopNotifications();
  }, [suggestDesktopNotifications]);

  const partnerChannelId = partnerChannelIdParam
    ? Number(partnerChannelIdParam)
    : null;
  const conversationId =
    partnerChannelId || !conversationIdParam
      ? null
      : Number(conversationIdParam);

  const [newChatOpen, setNewChatOpen] = useState(false);
  const [notesReady, setNotesReady] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [resolvedChannel, setResolvedChannel] = useState(null);
  const [partnerChannels, setPartnerChannels] = useState([]);
  const [canManageSettings, setCanManageSettings] = useState(false);
  const prevConvRef = useRef(null);
  const idleTimerRef = useRef(null);
  const [searchParams] = useSearchParams();
  const focusMessageId = searchParams.get("message");

  // Route → workspace only (omit selectedOrgId so switcher isn't fought mid-navigation).
  useEffect(() => {
    if (orgId) {
      setSelectedOrgId(orgId);
    }
  }, [orgId, setSelectedOrgId]);

  const { conversations, allConversations, loading, refresh, setConversations } =
    useDmConversations(orgId, {
      unreadOnly,
    });
  const { subscribe: subscribePartner, ensureConnected } = usePartnerSocket();

  useEffect(() => {
    ensureConnected?.();
  }, [ensureConnected]);

  const refreshPartnerChannels = useCallback(async () => {
    if (!accessToken || !orgId) return;
    try {
      const list = await listPartnerChannels(accessToken, orgId);
      setPartnerChannels(
        (list || []).map((c) => ({
          ...c,
          conversation_id: c.conversation_id || `partner:${c.channel_id}`,
          type: "partner",
          peer: null,
          is_self: false,
          subtitle: c.partner_org?.name
            ? `with ${c.partner_org.name}`
            : "Partner",
        }))
      );
    } catch {
      setPartnerChannels([]);
    }
  }, [accessToken, orgId]);

  useEffect(() => {
    if (!accessToken || !orgId) {
      setCanManageSettings(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const meRes = await axios.get(`${API_BASE_URL}/org/${orgId}/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (cancelled) return;
        const caps = new Set(
          Array.isArray(meRes.data?.capabilities)
            ? meRes.data.capabilities
            : []
        );
        setCanManageSettings(
          Boolean(meRes.data?.is_owner) ||
            meRes.data?.role === "admin" ||
            caps.has("org.manage_settings")
        );
      } catch {
        if (!cancelled) setCanManageSettings(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId]);

  useEffect(() => {
    if (!accessToken || !orgId) return;
    let cancelled = false;
    (async () => {
      try {
        await ensureDmNotes(accessToken, orgId);
        try {
          await ensureChannelDefaults(accessToken, orgId);
        } catch (err) {
          console.error("Failed to ensure channel defaults:", err);
        }
        if (!cancelled) {
          setNotesReady(true);
          await refresh();
          await refreshPartnerChannels();
        }
      } catch (err) {
        console.error("Failed to ensure notes DM:", err);
        if (!cancelled) setNotesReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId, refresh, refreshPartnerChannels]);

  useEffect(() => {
    if (!subscribePartner) return undefined;
    return subscribePartner((event, payload) => {
      if (
        event === "partner.message" ||
        event === "partner.message.updated" ||
        event === "partner.message.deleted" ||
        event === "partner.read" ||
        event === "partner.channel.updated"
      ) {
        if (payload?.org_id && String(payload.org_id) !== String(orgId)) {
          // Still refresh — member may be viewing from this org's room.
        }
        refreshPartnerChannels();
      }
    });
  }, [subscribePartner, refreshPartnerChannels, orgId]);

  const mergedConversations = useMemo(
    () => [...(conversations || []), ...partnerChannels],
    [conversations, partnerChannels]
  );

  const currentUserId = useMemo(() => {
    try {
      if (!accessToken) return null;
      const payload = JSON.parse(atob(accessToken.split(".")[1]));
      return payload?.sub || null;
    } catch {
      return null;
    }
  }, [accessToken]);

  const selectedFromList = useMemo(() => {
    if (!conversationId) return null;
    return (
      allConversations.find(
        (c) => Number(c.conversation_id) === conversationId
      ) || null
    );
  }, [allConversations, conversationId]);

  useEffect(() => {
    setResolvedChannel(null);
    if (!accessToken || !orgId || !conversationId || selectedFromList) return;
    let cancelled = false;
    (async () => {
      try {
        const channel = await getChannel(accessToken, orgId, conversationId);
        if (!cancelled && channel) {
          setResolvedChannel({
            ...channel,
            peer: null,
            is_self: false,
          });
        }
      } catch (err) {
        const fromNav = location.state?.channel;
        if (
          !cancelled &&
          fromNav &&
          Number(fromNav.conversation_id) === conversationId
        ) {
          setResolvedChannel({
            ...fromNav,
            type: "channel",
            peer: null,
            is_self: false,
          });
        } else if (!cancelled) {
          console.error("Failed to resolve channel:", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId, conversationId, selectedFromList, location.state]);

  const selected = selectedFromList || resolvedChannel;

  const purgeIfEmpty = async (conv) => {
    if (!accessToken || !orgId || !conv) return;
    if (conv.is_self || conv.type === "channel") return;
    if ((conv.message_count || 0) > 0 || conv.last_message) return;
    try {
      await deleteDmConversation(accessToken, orgId, conv.conversation_id);
      await refresh();
    } catch {
      /* ignore — may already have messages */
    }
  };

  // Purge previous empty chat on leave; idle purge while focused on empty
  useEffect(() => {
    const prevId = prevConvRef.current;
    if (prevId && prevId !== conversationId) {
      const prev = allConversations.find(
        (c) => Number(c.conversation_id) === Number(prevId)
      );
      purgeIfEmpty(prev);
    }
    prevConvRef.current = conversationId;

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (
      selected &&
      !selected.is_self &&
      selected.type !== "channel" &&
      !selected.last_message &&
      (selected.message_count || 0) === 0
    ) {
      idleTimerRef.current = setTimeout(() => {
        purgeIfEmpty(selected).then(() => {
          navigate(`/o/${encodeURIComponent(orgId)}/messages`);
        });
      }, DM_EMPTY_IDLE_MS);
    }
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, selected?.message_count, selected?.last_message]);

  const handleSelect = async (c) => {
    if (c?.type === "partner") {
      navigate(
        `/o/${encodeURIComponent(orgId)}/messages/partner/${c.channel_id}`
      );
      return;
    }
    if (selected && Number(selected.conversation_id) !== Number(c.conversation_id)) {
      await purgeIfEmpty(selected);
    }
    navigate(`/o/${encodeURIComponent(orgId)}/messages/${c.conversation_id}`);
  };

  const selectedPartnerId = partnerChannelId
    ? `partner:${partnerChannelId}`
    : conversationId;

  const handleNewChatCreated = async (conv) => {
    await refresh();
    navigate(
      `/o/${encodeURIComponent(orgId)}/messages/${conv.conversation_id}`
    );
  };

  const handleMarkAllRead = async () => {
    if (!accessToken || !orgId) return;
    try {
      await markAllDmRead(accessToken, orgId);
      await refresh();
    } catch (err) {
      console.error("Failed to mark all read:", err);
    }
  };

  const handleToggleMute = async (conversation, muted) => {
    const cid = conversation?.conversation_id ?? conversationId;
    if (!accessToken || !orgId || !cid) return;
    try {
      await setDmMuted(accessToken, orgId, cid, muted);
      setConversationMutedLocal(cid, muted);
      await refresh();
    } catch (err) {
      console.error("Failed to toggle mute:", err);
    }
  };

  const threadOpen = Boolean(conversationId || partnerChannelId);
  const inboxHref = `/o/${encodeURIComponent(orgId)}/messages`;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 overflow-x-hidden border-t border-border">
      <aside
        className={cn(
          "shrink-0 border-r border-border flex-col bg-muted/20 min-w-0",
          threadOpen ? "hidden md:flex md:w-72" : "flex w-full md:w-72"
        )}
      >
        <div className="px-4 py-3 border-b border-border space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h1 className="font-semibold text-base">Messages</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Joined DMs, groups, channels, and partner spaces.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 h-10 md:h-8"
              onClick={() => setNewChatOpen(true)}
            >
              <SquarePen className="h-3.5 w-3.5 mr-1" />
              New
            </Button>
          </div>
          {!threadOpen ? <AddToHomeScreenBanner /> : null}
          <MessageSearchBar orgId={orgId} />
          <div className="flex flex-wrap items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant={unreadOnly ? "default" : "ghost"}
              className="h-10 text-xs px-2 md:h-7"
              onClick={() => setUnreadOnly((v) => !v)}
            >
              Unread
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-10 text-xs px-2 md:h-7"
              onClick={handleMarkAllRead}
              title="Mark all as read"
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              Mark all read
            </Button>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col">
          <DmConversationSections
            conversations={mergedConversations}
            loading={loading || !notesReady}
            selectedId={selectedPartnerId}
            onSelect={handleSelect}
            onToggleMute={handleToggleMute}
            partnerEmptyMessage={
              canManageSettings ? (
                <>
                  No partner channels yet.{" "}
                  <Link
                    to={`/orgs/${encodeURIComponent(orgId)}/settings?section=partners`}
                    className="underline underline-offset-2 hover:text-foreground"
                  >
                    Manage Partner spaces
                  </Link>
                </>
              ) : (
                "No partner channels yet. Ask a workspace admin to set up Partner spaces."
              )
            }
          />
        </div>
      </aside>
      <main
        className={cn(
          "flex-1 min-w-0 min-h-0 flex-col overflow-x-hidden",
          threadOpen ? "flex" : "hidden md:flex"
        )}
      >
        {partnerChannelId ? (
          <PartnerThread
            orgId={orgId}
            channelId={partnerChannelId}
            onBack={() => navigate(inboxHref)}
          />
        ) : conversationId ? (
          <DmThread
            orgId={orgId}
            conversationId={conversationId}
            peer={selected?.peer}
            isSelf={!!selected?.is_self}
            conversation={selected}
            focusMessageId={focusMessageId}
            onBack={() => navigate(inboxHref)}
            onConversationChange={(next) => {
              if (next?.left) {
                navigate(`/o/${encodeURIComponent(orgId)}/messages`);
                refresh();
                return;
              }
              if (next?.conversation_id && next.navigate) {
                const purgeId = next.purge_conversation_id;
                navigate(
                  `/o/${encodeURIComponent(orgId)}/messages/${next.conversation_id}`
                );
                (async () => {
                  if (purgeId && accessToken && orgId) {
                    try {
                      await deleteDmConversation(accessToken, orgId, purgeId);
                    } catch {
                      /* still has messages or already gone */
                    }
                  }
                  await refresh();
                })();
                return;
              }
              if (next?.conversation_id) {
                const cid = Number(next.conversation_id);
                setConversations((prev) =>
                  prev.map((c) =>
                    Number(c.conversation_id) === cid ? { ...c, ...next } : c
                  )
                );
                setResolvedChannel((prev) =>
                  prev && Number(prev.conversation_id) === cid
                    ? { ...prev, ...next }
                    : prev
                );
              }
              refresh();
            }}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm p-8">
            Select a conversation
          </div>
        )}
      </main>

      <NewDmChatDialog
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
        orgId={orgId}
        accessToken={accessToken}
        currentUserId={currentUserId}
        onCreated={handleNewChatCreated}
      />
    </div>
  );
}
